import axios from 'axios'

const IS_PROD = process.env.VUE_APP_IS_PROD === 'true'
const API_BASE_URL = IS_PROD
  ? process.env.VUE_APP_BACKEND_URL_PROD
  : process.env.VUE_APP_BACKEND_URL_DEV

const RATE_LIMIT = {
  maxRequests: 40,
  perMilliseconds: 1000,
  requestQueue: [],
  lastRequestTime: 0
}

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000
})

axiosInstance.interceptors.request.use(
  config => {
    if (API_BASE_URL && config.url && config.url.startsWith(API_BASE_URL + '/')) {
      config.url = config.url.substring(API_BASE_URL.length)
    }
    return config
  },
  error => Promise.reject(error)
)

const rateLimit = async config => {
  const now = Date.now()
  const timeSinceLastRequest = now - RATE_LIMIT.lastRequestTime

  if (timeSinceLastRequest < RATE_LIMIT.perMilliseconds) {
    const delay = RATE_LIMIT.perMilliseconds - timeSinceLastRequest
    await new Promise(resolve => setTimeout(resolve, delay))
  }

  RATE_LIMIT.lastRequestTime = Date.now()
  return config
}

async function getCsrfToken () {
  try {
    const response = await fetch(`${API_BASE_URL}/csrf-token`)
    if (!response.ok) {
      console.error('Failed to fetch CSRF token:', response.statusText)
      return null
    }
    const data = await response.json()
    return data.token
  } catch (error) {
    console.error('Error fetching CSRF token:', error)
    return null
  }
}

axiosInstance.interceptors.request.use(
  async config => {
    await rateLimit(config)

    if (config.method !== 'get') {
      const token = await getCsrfToken()
      if (token) {
        config.headers['X-CSRF-Token'] = token
      }
    }

    return config
  },
  error => Promise.reject(error)
)

const retryRequest = (error, delay) => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve(axiosInstance(error.config))
    }, delay)
  })
}

axiosInstance.interceptors.response.use(
  response => response,
  error => {
    if (error.response) {
      const status = error.response.status

      switch (status) {
        case 429:
          console.error('Rate limit exceeded')
          return retryRequest(error, Math.min(1000 * Math.random(), 5000))

        case 401:
          console.error('Authentication error')
          break

        case 403:
          console.error('Authorization error')
          break

        case 500:
          console.error('Server error')
          break

        default:
          console.error(`Error: ${status}`)
      }
    } else if (error.request) {
      console.error('Network error')
    }
    return Promise.reject(error)
  }
)

export default axiosInstance
